/**
 * Dashboard Filter Persistence
 *
 * Utilities for saving and loading dashboard filters to/from localStorage
 * with proper validation to prevent malicious data injection
 */

import { analyticsQuerySchema } from '@skewb-works/contracts';
import { z } from 'zod';
import type { DashboardFilters } from '../types/analytics';

const STORAGE_KEY = 'dashboard-filters';

/**
 * Zod schema for validating dashboard filters in localStorage
 *
 * Extends the contract schema by making all fields required,
 * since localStorage should always have complete filter state.
 */
const DashboardFiltersSchema = z.object({
  // Reuse contract field schemas but make them required
  dateRange: analyticsQuerySchema.shape.dateRange.unwrap(),
  jobType: analyticsQuerySchema.shape.jobType.unwrap(),
  assignee: analyticsQuerySchema.shape.assignee.unwrap(),
  priority: analyticsQuerySchema.shape.priority.unwrap(),
});

/**
 * Load filters from localStorage with validation
 * Returns null if no saved filters exist, parsing fails, or validation fails
 *
 * Security measures:
 * - Validates dateRange against whitelist of allowed values
 * - Ensures string types for all fields
 * - Enforces length limits to prevent DoS
 * - Validates format (numeric IDs or 'all')
 * - Protects against XSS, SQL injection attempts, and type confusion
 */
export function loadFiltersFromStorage(): DashboardFilters | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    // Parse JSON
    const parsed = JSON.parse(stored);

    // Validate with Zod schema
    const result = DashboardFiltersSchema.safeParse(parsed);

    if (result.success) {
      return result.data;
    } else {
      // Log validation errors for debugging (but don't expose to user)
      console.warn('Invalid filter data in localStorage:', result.error.format());
      return null;
    }
  } catch (error) {
    console.error('Failed to load filters from localStorage:', error);
    return null;
  }
}

/**
 * Save filters to localStorage
 * Validates filters before saving to ensure data integrity
 */
export function saveFiltersToStorage(filters: DashboardFilters): void {
  try {
    // Validate before saving (defense in depth)
    const result = DashboardFiltersSchema.safeParse(filters);

    if (!result.success) {
      console.error('Attempted to save invalid filters:', result.error.format());
      return;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(result.data));
  } catch (error) {
    console.error('Failed to save filters to localStorage:', error);
  }
}

/**
 * Clear filters from localStorage
 */
export function clearFiltersFromStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear filters from localStorage:', error);
  }
}
